import React from 'react';
import { Project, Company } from '../types';
import { Card } from './ui/Card';
import { formatCurrency } from '../constants';
import { CompanyIcon } from './icons/CompanyIcon';
import { ChevronRightIcon } from './icons/ChevronRightIcon';
import { DragHandleIcon } from './icons/DragHandleIcon';
import { StatusBadge } from './ui/StatusBadge';

interface ObraListItemProps {
  project: Project;
  company?: Company;
  viewMode: 'grid' | 'list';
  onClick: () => void;
  // Drag and Drop handlers
  onDragStart: (id: string) => void;
  onDragEnter: (id: string) => void;
  onDragEnd: () => void;
}

export const ObraListItem: React.FC<ObraListItemProps> = ({ 
    project, 
    company, 
    viewMode, 
    onClick,
    onDragStart,
    onDragEnter,
    onDragEnd,
}) => {
  const totalCost = project.totalCost || 0;

  if (viewMode === 'grid') {
    return (
      <Card 
        className="hover:shadow-xl hover:border-brand-teal border-2 border-transparent transition-all duration-200 cursor-pointer flex flex-col justify-between"
        onClick={onClick}
      >
        <div className="p-4 flex-grow">
          <div className="flex justify-between items-start mb-2">
              <StatusBadge isLocked={project.isLocked} />
              {company?.logoUrl ? (
                  <img src={company.logoUrl} alt={company.name} className="h-8 max-w-[80px] object-contain" />
              ) : (
                  <CompanyIcon className="text-3xl text-gray-400" />
              )}
          </div>
          <h3 className="text-md font-bold text-gray-900 dark:text-dark-text-primary truncate" title={project.name}>
            {project.name}
          </h3>
          <p className="text-sm text-gray-600 dark:text-dark-text-secondary truncate" title={project.serviceDescription}>
            {project.serviceDescription}
          </p>
        </div>
        <div className="p-4 bg-gray-50 dark:bg-dark-surface border-t border-gray-200 dark:border-dark-tertiary">
            <p className="text-xs text-gray-500 dark:text-dark-text-secondary">Custo Total</p>
            <p className="text-lg font-bold text-brand-lime">{formatCurrency(totalCost)}</p>
        </div>
      </Card>
    );
  }

  // List view
  return (
    <div
      onClick={onClick}
      draggable
      onDragStart={() => onDragStart(project.id)}
      onDragEnter={() => onDragEnter(project.id)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => e.preventDefault()}
      className="bg-white dark:bg-dark-secondary rounded-lg shadow-sm hover:shadow-lg transition-shadow duration-200 cursor-pointer flex items-center p-4 group"
    >
      <div className="flex-shrink-0 text-gray-400 dark:text-dark-tertiary cursor-move mr-3" onMouseDown={(e) => e.stopPropagation()}>
        <DragHandleIcon className="text-2xl" />
      </div>
      <div className="flex-shrink-0 mr-4 h-12 w-12 rounded-md bg-gray-100 dark:bg-dark-tertiary flex items-center justify-center">
        {company?.logoUrl ? (
          <img src={company.logoUrl} alt={company.name} className="h-full w-full object-contain p-1" />
        ) : (
          <CompanyIcon className="text-3xl text-gray-400" />
        )}
      </div>
      <div className="flex-grow grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
        <div>
          <h3 className="text-md font-bold text-gray-900 dark:text-dark-text-primary truncate" title={project.name}>{project.name}</h3>
          <p className="text-sm text-gray-500 dark:text-dark-text-secondary truncate">{project.serviceDescription}</p>
           <div className="flex items-center gap-x-3 mt-1.5">
                <StatusBadge isLocked={project.isLocked} />
                {company?.address?.city && (
                    <p className="text-xs text-gray-400 dark:text-dark-text-secondary/80 truncate" title={`${company.address.city}, ${company.address.state}`}>
                        {company.address.city}, {company.address.state}
                    </p>
                )}
            </div>
        </div>
        <div className="text-sm">
          <p className="font-medium">Encarregado:</p>
          <p className="text-gray-600 dark:text-dark-text-secondary truncate" title={project.personInCharge}>{project.personInCharge}</p>
        </div>
         <div className="text-sm sm:text-right">
          <p className="font-medium">Custo Total:</p>
          <p className="text-lg font-bold text-brand-lime">{formatCurrency(totalCost)}</p>
        </div>
      </div>
       <div className="ml-4 flex-shrink-0">
          <ChevronRightIcon className="text-2xl text-gray-400" />
       </div>
    </div>
  );
};
